import { useEffect } from "react";

export const APP_RESUME_EVENT = "dashcam:app-resume";
export const APP_PAUSE_EVENT = "dashcam:app-pause";

type RefreshHandler = () => Promise<void> | void;

const refreshHandlers = new Set<RefreshHandler>();

/** Native WebView / Flutter app returned to the foreground. */
export function dispatchAppResume(): void {
  window.dispatchEvent(new Event(APP_RESUME_EVENT));
}

/** Native WebView / Flutter app is leaving the foreground. */
export function dispatchAppPause(): void {
  window.dispatchEvent(new Event(APP_PAUSE_EVENT));
}

export function subscribeForeground(onResume: () => void, onPause?: () => void): () => void {
  const resume = () => {
    onResume();
  };
  const vis = () => {
    if (document.visibilityState === "hidden") onPause?.();
    else onResume();
  };
  document.addEventListener("visibilitychange", vis);
  window.addEventListener("pageshow", resume);
  window.addEventListener("online", resume);
  window.addEventListener(APP_RESUME_EVENT, resume);
  if (onPause) window.addEventListener(APP_PAUSE_EVENT, onPause);
  return () => {
    document.removeEventListener("visibilitychange", vis);
    window.removeEventListener("pageshow", resume);
    window.removeEventListener("online", resume);
    window.removeEventListener(APP_RESUME_EVENT, resume);
    if (onPause) window.removeEventListener(APP_PAUSE_EVENT, onPause);
  };
}

export function registerPageRefresh(handler: RefreshHandler): () => void {
  refreshHandlers.add(handler);
  return () => {
    refreshHandlers.delete(handler);
  };
}

export function usePageRefresh(handler: RefreshHandler): void {
  useEffect(() => registerPageRefresh(handler), [handler]);
}

export async function runRegisteredRefresh(): Promise<void> {
  await Promise.allSettled([...refreshHandlers].map((fn) => Promise.resolve(fn())));
}
